import { api } from "../api";
import { escapeHtml } from "../moderation/format";
import type { Session } from "../session";
import { REPORT_CATEGORIES, type ReportCategory } from "../types";
import { confirmAction } from "./ConfirmDialog";
import { requireElement } from "./Hud";

/**
 * Lista de quem esta na cidade, para qualquer jogador logado, com o botao de
 * denunciar. Resolve quando o painel fecha.
 */
export function showPlayersPanel(session: Session): Promise<void> {
  return new Promise((resolve) => {
    const { token, user: me } = session;
    const root = requireElement<HTMLDivElement>("overlay-root");
    const overlay = document.createElement("div");
    overlay.className = "overlay";
    overlay.innerHTML = `
      <div class="panel mod-panel">
        <div class="profile-header">
          <div>
            <h1>Jogadores</h1>
            <p class="sub mod-sub" id="pl-count">Carregando…</p>
          </div>
          <button type="button" id="pl-refresh" class="secondary small">Atualizar</button>
        </div>
        <p class="notice hidden" id="pl-notice"></p>
        <div class="field mod-search">
          <input id="pl-filter" type="search" placeholder="Filtrar por nome…" autocomplete="off" />
        </div>
        <ul class="mod-list" id="pl-list"></ul>
        <p class="sub-hint">Alguém te ofendeu ou está atrapalhando? Denuncie: a moderação recebe junto as últimas mensagens da pessoa no chat.</p>
        <button type="button" id="pl-close" class="secondary">Fechar</button>
      </div>
    `;
    root.replaceChildren(overlay);

    const $ = <T extends HTMLElement>(sel: string) => overlay.querySelector(sel) as T;
    const list = $<HTMLUListElement>("#pl-list");
    const notice = $<HTMLParagraphElement>("#pl-notice");
    const filter = $<HTMLInputElement>("#pl-filter");
    let players: { id: string; name: string }[] = [];
    let dialogOpen = false;

    const setNotice = (text: string | null, level: "ok" | "error" = "ok") => {
      notice.className = `notice ${level}${text ? "" : " hidden"}`;
      notice.textContent = text ?? "";
    };

    const render = () => {
      const term = filter.value.trim().toLowerCase();
      const shown = players.filter((p) => !term || p.name.toLowerCase().includes(term));
      $<HTMLParagraphElement>("#pl-count").textContent =
        `${players.length} ${players.length === 1 ? "jogador" : "jogadores"} na cidade`;
      list.innerHTML =
        shown
          .map(
            (p) => `
          <li class="mod-row"><div class="mod-row-main">
            <div class="mod-who"><strong>${escapeHtml(p.name)}</strong></div>
            ${
              p.id === me.id
                ? `<span class="mod-handle">você</span>`
                : `<button type="button" class="secondary small danger" data-report="${p.id}">Denunciar</button>`
            }
          </div></li>`,
          )
          .join("") || `<li class="empty">${players.length ? "Ninguém com esse nome" : "Ninguém online"}</li>`;
    };

    const load = async () => {
      try {
        players = (await api.playersOnline(token)).players;
        render();
      } catch (error) {
        setNotice(error instanceof Error ? error.message : "Não foi possível carregar a lista.", "error");
      }
    };

    list.addEventListener("click", async (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-report]");
      const target = players.find((p) => p.id === button?.dataset.report);
      if (!target) return;
      dialogOpen = true;
      const result = await confirmAction({
        title: `Denunciar ${target.name}`,
        message: "A denúncia vai para a moderação com as últimas mensagens dessa pessoa no chat. Denúncias falsas também podem ser punidas.",
        confirmLabel: "Enviar denúncia",
        danger: true,
        fields: [
          {
            name: "category",
            label: "Motivo",
            type: "select",
            options: (Object.keys(REPORT_CATEGORIES) as ReportCategory[]).map((c) => ({ value: c, label: REPORT_CATEGORIES[c] })),
          },
          { name: "details", label: "O que aconteceu? (obrigatório em 'Outro motivo')", type: "textarea", maxLength: 300 },
        ],
        run: (v) => api.report(token, target.id, v.category as ReportCategory, v.details).then((r) => r.message),
      });
      dialogOpen = false;
      if (result !== null) setNotice(result, "ok");
    });

    filter.addEventListener("input", render);
    overlay.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") event.stopPropagation();
    });
    $<HTMLButtonElement>("#pl-refresh").addEventListener("click", () => {
      setNotice(null);
      void load();
    });

    const close = () => {
      document.removeEventListener("keydown", onKey);
      root.replaceChildren();
      resolve();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !dialogOpen) {
        event.preventDefault();
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    $<HTMLButtonElement>("#pl-close").addEventListener("click", close);

    void load();
  });
}
